/* =============================================================================
   VIEWS / ADMIN / TABLE-GUEST-NOTES — „Nussallergie" vom Tisch ins Cockpit
   =============================================================================
   S253. Der Gast trägt am Handy einen Hinweis für die Küche ein (phase_185,
   `table_guest_notes`); hier wird er gelesen — im KDS an der Runde, auf der
   Kanban-Karte und im Order-Detail.

   Zwei Entscheidungen von Jonas prägen dieses Modul:

   1. **Der Hinweis hängt an der BELEGUNG, nicht an der Runde.** Einmal
      eingetragen gilt er für jede Runde dieses Gastes, auch für die
      Nachbestellung um 21 Uhr.
   2. **Immer der aktuelle Stand, auch rückwirkend.** Deshalb gibt es KEINE
      Kopie an `order_items`: gelesen wird bei der Anzeige. Ändert der Gast den
      Hinweis nach dem Absenden, zeigt das KDS den neuen — und genau deshalb
      braucht dieses Modul ein Realtime-Abo und keinen einmaligen Ladevorgang.

   Ein bereits GEDRUCKTER Zettel kann das naturgemäß nicht nachvollziehen. Der
   Hinweis steht deshalb bewusst nicht auf dem Küchenbon (und nicht auf dem
   Beleg): wer nach Papier arbeitet, sieht die späte Änderung am Bildschirm.

   Aufbau wie `table-signals.js` (S245): EIN Modul-State, EIN Loader, EIN
   Realtime-Abo, Listener für die Sichten. Zwei Tabs, die dieselbe Liste
   getrennt laden, driften auseinander.
============================================================================ */

import { htmlEscape } from '../../utils/helpers.js';

/** Alle bekannten Hinweise (Tenant, letzte Tage). Überlebt Re-Render. */
let _notes = [];
let _loadedAt = 0;
let _inflight = null;
let _unsub = null;
let _visibilityBound = false;
const _listeners = new Set();

/* -------------------------------------------------------------------------- */
/* Pure Helfer (Vitest)                                                        */
/* -------------------------------------------------------------------------- */

/**
 * Pure — Anzeigename eines Hinweises.
 *
 * `guest_name`, wenn der Gast einen Vornamen hinterlegt hat; sonst schlicht
 * „Tischgast". Die Nummerierung „Gast 2/3" der Gast-Seite leitet sich aus deren
 * Warenkorb-Reihenfolge ab und lässt sich im Cockpit nicht verlässlich
 * nachbilden — eine geratene Nummer wäre schlimmer als gar keine, weil sie am
 * Tisch auf jemand anderen zeigen könnte.
 */
export function guestNoteLabel(note) {
  const n = note && (note.guest_name || note.guestName);
  return (n && String(n).trim()) ? String(n).trim() : 'Tischgast';
}

/**
 * Pure — Realtime-Payload auf die Liste anwenden.
 *
 * Ein DELETE ist der Normalfall und kein Randfall: der Gast wischt seinen Text
 * weg, und die RPC löscht die Zeile. Der Hinweis muss dann überall verschwinden.
 *
 * @returns {Array} neue Liste (nie dieselbe Referenz, damit Vergleiche greifen)
 */
export function applyGuestNotePayload(list, payload) {
  const cur = Array.isArray(list) ? list.slice() : [];
  const ev = payload && payload.eventType;
  const row = (ev === 'DELETE') ? (payload && payload.old) : (payload && payload.new);
  if (!row || !row.id) return cur;
  const idx = cur.findIndex(n => n.id === row.id);
  if (ev === 'DELETE') {
    if (idx >= 0) cur.splice(idx, 1);
    return cur;
  }
  if (idx >= 0) cur[idx] = { ...cur[idx], ...row };
  else cur.push(row);
  cur.sort((a, b) => String(a.created_at || '').localeCompare(String(b.created_at || '')));
  return cur;
}

/** Pure — die Hinweise EINER Belegung. */
export function notesForOccupancy(list, occupancyId) {
  if (!occupancyId) return [];
  return (Array.isArray(list) ? list : [])
    .filter(n => n && n.occupancy_id === occupancyId && String(n.note || '').trim());
}

/**
 * Pure — die Hinweise der Gäste, die in DIESER Runde etwas bestellt haben.
 *
 * Das KDS zeigt eine Kachel je Runde. Ein Hinweis, der zu keiner Position
 * dieser Runde gehört, gehört auch nicht auf die Kachel — sonst stünde an der
 * Getränke-Nachbestellung von Gast 3 die Allergie von Gast 1.
 *
 * Kennt keine Position einen `guest_key` (Cockpit-Bestellung am Tisch, oder
 * eine Runde von vor phase_177), fallen ALLE Hinweise der Belegung zurück: dann
 * ist die Zuordnung unbekannt, und im Zweifel muss die Küche die Allergie
 * sehen. Lieber ein Hinweis zu viel als einer zu wenig.
 */
export function notesForRound(list, occupancyId, items) {
  const all = notesForOccupancy(list, occupancyId);
  if (!all.length) return [];
  const keys = new Set();
  for (const it of (Array.isArray(items) ? items : [])) {
    const k = it && (it.guestKey || it.guest_key);
    if (k) keys.add(String(k));
  }
  if (!keys.size) return all;
  const hit = all.filter(n => keys.has(String(n.guest_key || n.guestKey || '')));
  return hit.length ? hit : [];
}

/* -------------------------------------------------------------------------- */
/* Laden + Realtime                                                            */
/* -------------------------------------------------------------------------- */

function _adapter() {
  return (window.App && window.App.storage && window.App.storage.tableGuestNotes) || null;
}

/** Modul-State (nicht neu geladen). */
export function guestNotes() { return _notes; }

/** Auf Änderungen hören. Gibt eine Abmelde-Funktion zurück. */
export function onGuestNotesChanged(fn) {
  if (typeof fn !== 'function') return () => {};
  _listeners.add(fn);
  return () => { _listeners.delete(fn); };
}

function _notify() {
  for (const fn of _listeners) {
    try { fn(_notes); } catch (e) { console.warn('[table-guest-notes] Listener:', e); }
  }
}

/** Nachladen. Höchstens alle 15 s — das Realtime-Abo ist der eigentliche Weg. */
export function refreshGuestNotes(opts) {
  const force = !!(opts && opts.force);
  const ad = _adapter();
  if (!ad) return Promise.resolve(_notes);
  if (!force && Date.now() - _loadedAt < 15000) return Promise.resolve(_notes);
  if (_inflight) return _inflight;
  _loadedAt = Date.now();
  _inflight = ad.listRecent()
    .then(rows => {
      _notes = Array.isArray(rows) ? rows : [];
      _notify();
      return _notes;
    })
    .catch(e => { console.warn('[table-guest-notes] laden:', e && e.message); return _notes; })
    .finally(() => { _inflight = null; });
  return _inflight;
}

function _ensureRealtime() {
  if (_unsub) return;
  const st = window.App && window.App.storage;
  if (!st || typeof st.subscribeTableGuestNotes !== 'function') return;
  try {
    _unsub = st.subscribeTableGuestNotes((payload) => {
      _notes = applyGuestNotePayload(_notes, payload);
      _notify();
    });
  } catch (e) { console.warn('[table-guest-notes] Realtime:', e && e.message); }
}

/** Catch-up, wenn der Tab wieder sichtbar wird (dasselbe Muster wie S246/T3). */
function _bindVisibilityCatchUp() {
  if (_visibilityBound || typeof document === 'undefined') return;
  _visibilityBound = true;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') refreshGuestNotes({ force: true });
  });
}

/**
 * Einmalige Verdrahtung für eine Sicht (KDS, Theke, Board).
 *
 * `onChange` wird bei jeder Änderung gerufen — die Sicht zeichnet dann selbst
 * neu, wie sie es ohnehin tut. Gibt eine Abmelde-Funktion zurück.
 */
export function initGuestNotes(onChange) {
  _ensureRealtime();
  _bindVisibilityCatchUp();
  const off = onGuestNotesChanged(onChange);
  refreshGuestNotes();
  return off;
}

/**
 * S257 — das Abo gehört an den Boot, nicht an die erste Sicht.
 *
 * Dieselbe Wurzel wie bei den Signalen: der Coordinator hält einen Channel je
 * Browser, und zwar im Master-Tab. Stand der Master auf dem Dashboard, gab es
 * den Channel nirgends — Hinweis-Änderungen blieben im Follower-KDS über vier
 * Minuten alt (S255-Cockpit-Retest §6). Beim Hinweis wiegt das doppelt, weil er
 * ausdrücklich **rückwirkend** gilt: eine späte Änderung MUSS ankommen, sonst
 * arbeitet die Küche mit dem alten Stand weiter.
 *
 * Bewusst eine eigene Funktion statt `initGuestNotes(() => {})` aus dem Boot:
 * das hinterliesse einen No-Op-Listener, den niemand je wieder abmeldet.
 *
 * @returns {(function():void)|null} Abmelder, oder `null`, wenn (noch) kein Abo
 *   zustande kam — dann versucht es der nächste `attachRealtimeSync()` erneut.
 */
export function startGuestNotesRealtime() {
  _ensureRealtime();
  _bindVisibilityCatchUp();
  refreshGuestNotes();
  return _unsub ? stopGuestNotesRealtime : null;
}

/**
 * Gegenstück für `detachRealtimeSync()`. Räumt nur den Channel ab; der
 * Sichtbarkeits-Nachlauf bleibt als zweite Verteidigungslinie stehen (siehe die
 * gleichlautende Begründung in `table-signals.js`).
 */
export function stopGuestNotesRealtime() {
  if (!_unsub) return;
  try { _unsub(); } catch (e) { console.warn('[table-guest-notes] Abmelden:', e && e.message); }
  _unsub = null;
}

/** Test-Hook: hängt gerade ein Realtime-Abo? */
export function _hasGuestNotesRealtimeForTests() { return !!_unsub; }

/** Test-Hook: Abo, Liste, Listener und Lade-Drossel zurücksetzen. */
export function _resetGuestNotesForTests() {
  stopGuestNotesRealtime();
  _notes = [];
  _loadedAt = 0;
  _inflight = null;
  _listeners.clear();
}

/* -------------------------------------------------------------------------- */
/* Render                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * HTML-Block für eine Liste von Hinweisen. Leere Liste → leerer String.
 *
 * Bewusst deutlich gesetzt (eigene Fläche, Symbol, Name): eine Allergie ist
 * kein Kleingedrucktes. Aber ohne Farb-Alarm — die Küche sieht diesen Block im
 * Regelfall, nicht im Ausnahmefall, und ein dauerhaft rotes Feld stumpft ab.
 *
 * @param {Array}  list
 * @param {object} [opts] `{ cls }` — Klassen-Präfix der Sicht (kds/oc).
 */
export function renderGuestNotesHtml(list, opts = {}) {
  const rows = (Array.isArray(list) ? list : []).filter(n => n && String(n.note || '').trim());
  if (!rows.length) return '';
  const cls = opts.cls || 'gn';
  return `<div class="${cls}-guest-notes">${rows.map(n => `
    <div class="${cls}-guest-note">
      <span class="${cls}-guest-note-who">✏️ ${htmlEscape(guestNoteLabel(n))}</span>
      <span class="${cls}-guest-note-text">${htmlEscape(String(n.note).trim())}</span>
    </div>`).join('')}</div>`;
}

/** Test-Hook: Modul-State setzen/zurücksetzen. */
export function _setGuestNotesForTests(list) {
  _notes = Array.isArray(list) ? list : [];
}

if (typeof window !== 'undefined') {
  window.App = window.App || {};
  // storage.js ist ein Plain-Script ohne ESM-Importe — der Reconnect-Catch-up
  // erreicht das Modul deshalb über `window.App`, wie bei den Signalen.
  window.App.tableGuestNotes = { refresh: refreshGuestNotes, notes: guestNotes };
}
